import React, { useMemo, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { ClipboardCheck, Ban, ArrowRight, Loader2, ShieldAlert, AlertTriangle, Download, TrendingUp } from "lucide-react";
import { SKIP_LABELS, REASON_HELP, heldReason, rowKey, flagExplanation } from "../lib/importReasons";

const cleanCell = (v) => {
  if (v == null) return "";
  const s = String(v).trim();
  if (["nan", "none", "nat", "null"].includes(s.toLowerCase())) return "";
  return /^-?\d+\.0+$/.test(s) ? s.split(".")[0] : s;
};

const label = (r) => {
  const raw = r.raw || {};
  return Object.values(raw).map(cleanCell).filter(Boolean).slice(0, 3).join(" · ").slice(0, 90) || `Row #${(r.idx ?? 0) + 1}`;
};

const csvCell = (v) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Confirm & Reconcile — the commit "receipt": committed + held = reviewed rows,
// a plain-language held breakdown, a required acknowledgement, and a typed override
// for any blocked line the reviewer insists on committing.
export function ImportConfirmDialog({ open, onClose, rows = [], totalRows, committing, onConfirm, senseCheck }) {
  const [acks, setAcks] = useState({});
  const [ackAll, setAckAll] = useState(false);
  useEffect(() => { if (open) { setAcks({}); setAckAll(false); } }, [open]);

  const blockedIncluded = useMemo(
    () => rows.filter((r) => r.blocked && r.include && r.pillar && r.category),
    [rows]);

  const { committed, heldByReason, heldTotal } = useMemo(() => {
    const byReason = {};
    let c = 0, h = 0;
    rows.forEach((r) => {
      const hasAck = !!(acks[rowKey(r)] || "").trim();
      const reason = heldReason(r, hasAck);
      if (reason === null) { c += 1; return; }
      h += 1;
      byReason[reason] = (byReason[reason] || 0) + 1;
    });
    return { committed: c, heldByReason: byReason, heldTotal: h };
  }, [rows, acks]);

  const heldRows = useMemo(() => rows.map((r) => {
    const hasAck = !!(acks[rowKey(r)] || "").trim();
    const reason = heldReason(r, hasAck);
    return reason ? { r, reason } : null;
  }).filter(Boolean), [rows, acks]);

  const downloadHeld = () => {
    const header = ["Row #", "Sheet", "Source", "Pillar", "Category", "Activity", "Value", "Unit", "Period", "Site", "Reference", "Held reason", "Rule", "Evidence", "Suggested fix"];
    const lines = [header.join(",")];
    heldRows.forEach(({ r, reason }) => {
      const ex = flagExplanation(r) || {};
      lines.push([(r.idx ?? 0) + 1, r.__sheet || "", label(r), r.pillar || "", r.category || "", r.activity_type || "", cleanCell(r.value), r.unit || "", r.period || "", r.facility || "", r.reference || "", SKIP_LABELS[reason] || reason, ex.rule || "", ex.evidence || "", ex.fix || ""].map(csvCell).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "held_rows.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const reviewed = rows.length;
  const allBlockedAcked = blockedIncluded.every((r) => (acks[rowKey(r)] || "").trim().length >= 3);
  const canConfirm = !committing && ackAll && allBlockedAcked && committed > 0;

  const confirm = () => {
    const map = {};
    blockedIncluded.forEach((r) => { const a = (acks[rowKey(r)] || "").trim(); if (a) map[rowKey(r)] = a; });
    onConfirm(map);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[94vw] max-w-[640px] max-h-[88vh] overflow-y-auto" data-testid="import-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-accent" /> Confirm &amp; reconcile</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Batch sense-check — advisory warning when a batch swings the inventory a lot */}
          {senseCheck && senseCheck.level && senseCheck.level !== "ok" && senseCheck.message && (
            <div className={`card-flat p-3 flex items-start gap-2 ${senseCheck.level === "large" ? "border-red-500/40 bg-red-500/5" : "border-[#FFB800]/40 bg-[#FFB800]/5"}`} data-testid="sense-check-banner">
              <TrendingUp className={`h-4 w-4 mt-0.5 shrink-0 ${senseCheck.level === "large" ? "text-red-400" : "text-[#FFB800]"}`} />
              <div className="min-w-0">
                <div className="text-xs font-semibold inline-flex items-center gap-1.5">
                  Batch sense-check
                  <Badge variant={senseCheck.level === "large" ? "danger" : "warning"} className="text-[9px]" data-testid="sense-check-level">
                    {senseCheck.level === "large" ? "Large swing" : senseCheck.level === "new" ? "New period" : "Notable"}
                  </Badge>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="sense-check-message">{senseCheck.message}</p>
                {senseCheck.drift && senseCheck.drift.message && senseCheck.drift.message !== senseCheck.message && (
                  <p className="text-[11px] text-[#FFB800] mt-1 inline-flex items-start gap-1" data-testid="sense-check-drift"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{senseCheck.drift.message}</p>
                )}
                {Array.isArray(senseCheck.history) && senseCheck.history.length > 1 && (
                  <div className="flex items-end gap-2 mt-1.5" data-testid="sense-check-history">
                    {senseCheck.history.map((h) => (
                      <div key={h.year} className="text-center">
                        <div className={`text-[11px] font-mono ${h.is_batch_year ? "text-foreground font-semibold" : "text-muted-foreground"}`}>{Math.round(h.projected_tonnes)}</div>
                        <div className={`text-[9px] ${h.is_batch_year ? "text-accent" : "text-muted-foreground"}`}>{h.year}{h.is_batch_year ? "*" : ""}</div>
                      </div>
                    ))}
                    <span className="text-[9px] text-muted-foreground self-center">tCO₂e/yr {senseCheck.history.some((h) => h.is_batch_year) ? "(*after this batch)" : ""}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reconciliation receipt */}
          <div className="card-flat p-3 space-y-2" data-testid="reconcile-receipt">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Reviewed rows</span>
              <span className="font-mono font-semibold" data-testid="reconcile-total">{reviewed}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="inline-flex items-center gap-1.5 text-[#00E65B]">Committing</span>
              <span className="font-mono font-semibold text-[#00E65B]" data-testid="reconcile-committed">{committed}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="inline-flex items-center gap-1.5 text-[#FFB800]">Held</span>
              <span className="font-mono font-semibold text-[#FFB800]" data-testid="reconcile-held">{heldTotal}</span>
            </div>
            <div className="h-px bg-border" />
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">committed + held</span>
              <span className={`font-mono font-bold ${committed + heldTotal === reviewed ? "text-[#00E65B]" : "text-red-400"}`} data-testid="reconcile-check">
                {committed} + {heldTotal} = {committed + heldTotal} {committed + heldTotal === reviewed ? "✓" : "≠ " + reviewed}
              </span>
            </div>
            {totalRows != null && totalRows !== reviewed && (
              <p className="text-[10px] text-muted-foreground" data-testid="reconcile-file-note">
                File had {totalRows} rows in total; {reviewed} were analysed for review (the rest were on empty or unreadable sheets).
              </p>
            )}
          </div>

          {/* Held breakdown — plain language */}
          {heldTotal > 0 && (
            <div data-testid="reconcile-held-breakdown">
              <div className="label-eyebrow text-[10px] mb-1.5">What's being held, and why</div>
              <div className="space-y-1">
                {Object.entries(heldByReason).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                  <div key={k} className="flex items-start gap-2 text-xs card-flat px-2.5 py-1.5" data-testid={`reconcile-held-${k}`}>
                    <Badge variant={k === "blocked" ? "danger" : k === "not_calculated" ? "outline" : "warning"} className="text-[9px] shrink-0">{SKIP_LABELS[k] || k}: {n}</Badge>
                    <span className="text-muted-foreground leading-tight">{REASON_HELP[k] || ""}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between gap-2 mt-1.5 flex-wrap">
                <p className="text-[10px] text-muted-foreground">Held rows aren't dropped — recover them later from Import Runs without re-uploading.</p>
                <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={downloadHeld} data-testid="confirm-download-held"><Download className="h-3 w-3" /> Download held rows</Button>
              </div>
              <details className="mt-1.5 text-[11px]" data-testid="held-rows-detail">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Show the {heldTotal} held row{heldTotal === 1 ? "" : "s"} and why</summary>
                <div className="mt-1.5 space-y-1 max-h-56 overflow-y-auto pr-1">
                  {heldRows.map(({ r, reason }) => {
                    const ex = flagExplanation(r);
                    return (
                      <div key={rowKey(r)} className="card-flat px-2.5 py-1.5" data-testid={`held-row-${rowKey(r)}`}>
                        <div className="flex items-center gap-2">
                          <Badge variant={reason === "blocked" ? "danger" : reason === "not_calculated" ? "outline" : "warning"} className="text-[9px] shrink-0">{SKIP_LABELS[reason] || reason}</Badge>
                          <span className="truncate text-foreground">{label(r)}</span>
                        </div>
                        {ex && (
                          <div className="mt-0.5 pl-1 text-[10px] leading-tight text-muted-foreground">
                            <div><span className="font-semibold text-foreground">Rule:</span> {ex.rule}</div>
                            <div><span className="font-semibold text-foreground">Evidence:</span> {ex.evidence}</div>
                            <div><span className="font-semibold text-[#00E65B]">Fix:</span> {ex.fix}</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </details>
            </div>
          )}

          {/* Typed override for blocked rows the reviewer insists on committing */}
          {blockedIncluded.length > 0 && (
            <div className="card-flat p-3 border-red-500/30 bg-red-500/5 space-y-2" data-testid="override-section">
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-400"><ShieldAlert className="h-3.5 w-3.5" /> {blockedIncluded.length} blocked line{blockedIncluded.length === 1 ? "" : "s"} ticked to commit as-is</div>
              <p className="text-[11px] text-muted-foreground">These failed the unit/scale guard. To commit them unchanged you must type a short acknowledgement for each — it's recorded against the record for audit.</p>
              {blockedIncluded.map((r) => (
                <div key={rowKey(r)} className="space-y-1" data-testid={`override-row-${rowKey(r)}`}>
                  <div className="text-[11px] text-foreground inline-flex items-center gap-1"><Ban className="h-3 w-3 text-red-400" /> {label(r)}</div>
                  <div className="text-[10px] text-red-300/90">{r.blocked_reason}</div>
                  <Input
                    value={acks[rowKey(r)] || ""}
                    onChange={(e) => setAcks((p) => ({ ...p, [rowKey(r)]: e.target.value }))}
                    placeholder="Type why you're overriding (min 3 chars)"
                    className="h-8 text-xs"
                    data-testid={`override-input-${rowKey(r)}`}
                  />
                </div>
              ))}
            </div>
          )}

          <label className="flex items-start gap-2 text-xs cursor-pointer" data-testid="confirm-ack-label">
            <input type="checkbox" checked={ackAll} onChange={(e) => setAckAll(e.target.checked)} className="h-3.5 w-3.5 mt-0.5 accent-[hsl(var(--accent))]" data-testid="confirm-ack-checkbox" />
            <span className="text-muted-foreground">I've reviewed the held rows above and understand these <span className="text-foreground font-medium">{committed}</span> record{committed === 1 ? "" : "s"} will enter the inventory as <span className="text-[#FFB800] font-medium">Unverified</span>.</span>
          </label>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} disabled={committing} data-testid="confirm-cancel-btn">Back to review</Button>
            <Button variant="primary" onClick={confirm} disabled={!canConfirm} data-testid="confirm-commit-btn">
              {committing ? <><Loader2 className="h-4 w-4 animate-spin" /> Committing…</> : <>Commit {committed} record{committed === 1 ? "" : "s"} <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
